import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ForgotPasswordHelpModal from "../components/ForgotPasswordHelpModal";
import { loginUser } from "../api";
import { completeLoginAfterApi } from "../utils/completeLogin";
import AlertService from "../services/AlertServices";
import usePageTitle from "../hooks/usePageTitle";
import PageChrome from "../components/PageChrome";

// Layout Components
import Footer from "../components/Footer";
import CopyRight from "../components/CopyRight";

import "../assets/css/Login.css";
import "../assets/css/ForgotPassword.css";

const Login = () => {
  usePageTitle("Login", {
    description:
      "Login to your Dewangan Links account to view matches, send connection requests, and manage your profile.",
  });
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotHelp, setShowForgotHelp] = useState(false);
  const [lang, setLang] = useState("en");
  const navigate = useNavigate();

  // 🌐 TRANSLATION TEXTS
  const t = {
    en: {
      headline1: "Now find",
      headline2: "your life partner",
      headline3: "Easy and fast.",
      title: "Sign in to Matrimony",
      subtitle: "Enter your credentials to continue",
      userId: "User ID",
      placeholderUser: "Enter User ID, Email, or Mobile",
      password: "Password",
      placeholderPass: "Enter password",
      remember: "Remember me",
      signIn: "Sign In",
      noAccount: "Not a member?",
      register: "Register",
      requiredMsg: "Please enter both User ID and password.",
      signing: "Signing In...",
      forgotPassword: "Forgot password?",
    },

    hi: {
      headline1: "अब पाएँ",
      headline2: "अपना जीवनसाथी",
      headline3: "आसानी से और तेज़ी से।",
      title: "मैट्रिमोनी में लॉगिन करें",
      subtitle: "जारी रखने के लिए अपनी जानकारी दर्ज करें",
      userId: "यूज़र आईडी",
      placeholderUser: "यूज़र आईडी, ईमेल या मोबाइल दर्ज करें",
      password: "पासवर्ड",
      placeholderPass: "पासवर्ड दर्ज करें",
      remember: "मुझे याद रखें",
      signIn: "लॉगिन",
      noAccount: "अभी सदस्य नहीं हैं?",
      register: "रजिस्टर करें",
      requiredMsg: "कृपया यूज़र आईडी और पासवर्ड दर्ज करें।",
      signing: "लॉगिन हो रहा है...",
      forgotPassword: "पासवर्ड भूल गए?",
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!identifier || !password) {
      AlertService.showError(t[lang].requiredMsg);
      setLoading(false);
      return;
    }

    try {
      const data = await loginUser({ identifier, password });
      const nextPath = await completeLoginAfterApi(data);

      AlertService.showSuccess(
        lang === "en" ? "Login successful!" : "लॉगिन सफल!",
      );

      navigate(nextPath);
    } catch (error) {
      AlertService.showError(error.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dw-login-page">
      <PageChrome />

      <section>
        <div className="login">
          <div className="container">
            <div className="row">
              <div className="inn">
                {/* LEFT SIDE */}
                <div className="lhs">
                  <div className="tit">
                    <h2>
                      {t[lang].headline1} <br />
                      <b>{t[lang].headline2}</b>
                      <br />
                      {t[lang].headline3}
                    </h2>
                  </div>

                  <div className="im">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/login-couple.png`}
                      alt=""
                    />
                  </div>

                  <div className="log-bg">&nbsp;</div>
                </div>

                {/* RIGHT SIDE */}
                <div className="rhs">
                  <div>
                    {/* 🌐 LANGUAGE SWITCHER */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "16px",
                        marginBottom: "25px",
                      }}
                    >
                      <button
                        onClick={() => setLang("en")}
                        className={
                          lang === "en" ? "active-lang-btn" : "lang-btn"
                        }
                      >
                        English
                      </button>

                      <button
                        onClick={() => setLang("hi")}
                        className={
                          lang === "hi" ? "active-lang-btn" : "lang-btn"
                        }
                      >
                        हिन्दी
                      </button>
                    </div>

                    <div className="form-tit">
                      <h4>{t[lang].subtitle}</h4>
                      <h1>{t[lang].title}</h1>

                      <p>
                        {t[lang].noAccount}{" "}
                        <Link to="/sign-up">{t[lang].register}</Link>
                      </p>
                    </div>

                    {/* LOGIN FORM */}
                    <div className="form-login">
                      <form onSubmit={handleSubmit}>
                        <div className="form-group">
                          <label className="lb">{t[lang].userId}</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder={t[lang].placeholderUser}
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            required
                          />
                        </div>

                        {/* UPDATED PASSWORD FIELD */}
                        <div
                          className="form-group"
                          style={{ position: "relative" }}
                        >
                          <label className="lb">{t[lang].password}</label>
                          <input
                            type={showPassword ? "text" : "password"}
                            className="form-control"
                            placeholder={t[lang].placeholderPass}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            style={{ paddingRight: "40px" }}
                          />
                          <span
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: "absolute",
                              right: "15px",
                              top: "35px", // aligns icon vertically
                              cursor: "pointer",
                              color: "#c9c9c9",
                              fontSize: "18px",
                            }}
                          >
                            <i
                              className={
                                showPassword ? "fa fa-eye-slash" : "fa fa-eye"
                              }
                            ></i>
                          </span>
                        </div>

                        <div className="form-group form-check">
                          <label className="form-check-label">
                            <input
                              className="form-check-input"
                              type="checkbox"
                            />{" "}
                            {t[lang].remember}
                          </label>
                        </div>

                        <p className="dw-login-forgot">
                          <button
                            type="button"
                            className="dw-login-forgot__btn"
                            onClick={() => setShowForgotHelp(true)}
                          >
                            {t[lang].forgotPassword}
                          </button>
                        </p>

                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? t[lang].signing : t[lang].signIn}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
                {/* RHS END */}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <CopyRight />

      <ForgotPasswordHelpModal
        show={showForgotHelp}
        onClose={() => setShowForgotHelp(false)}
        lang={lang}
      />
    </div>
  );
};

export default Login;
